import React from 'react';

import {FloatingPopover, useFloating} from '@pb/kit-component-floating';
import {withCssModule} from '@pb/kit-util-with-css-module';

import styles from './tooltip.component.module.styl';

import type {FloatingPopoverProps, UseFloatingProps, UseFloatingReturnType} from '@pb/kit-component-floating';
import type {ReactNode} from 'react';


const classNames = withCssModule(styles);

type _FloatingPopoverProps = Partial<Omit<FloatingPopoverProps, 'open' | 'listItemsRef' | 'getContext' | 'flip'>>;

export interface TooltipProps extends UseFloatingProps {

    color?: 'light' | 'dark',

    /**
     * An element the Dropdown should be triggered by and positioned at. Pass anchorProps and anchorRef to the anchor element and return the element itself
     * */
    anchor: (props: {
        anchorRef: UseFloatingReturnType['anchorRef'],
        /**
         * Can merge external props with the anchor element props
         * */
        getAnchorProps: UseFloatingReturnType['getAnchorProps'],
        positionRef: UseFloatingReturnType['positionRef'],
    }) => ReactNode,

    /**
     * Props for the FloatingPopover component
     * */
    popoverProps?: _FloatingPopoverProps,

    /**
     * Content of the tooltip
     * */
    children: ReactNode,
}


/**
 * Tooltip
 *
 * A tooltip is a popup that displays information related to an element when the element receives keyboard focus or the mouse hovers over it. It typically appears after a small delay and disappears when Escape is pressed or on mouse out.
 *
 * Tooltip widgets do not receive focus. A hover that contains focusable elements can be made using a non-modal dialog.
 *
 * **Features**
 *
 * * Shows a tooltip on hover or focus.
 * * Adds 'aria-describedby' to the anchor element.
 * * Extends the Floating, native 'div' component.
 *
 * **Accessibility**
 *
 * Implemented with `aria-describedby` and `role` attributes according to the [WAI-ARIA Tooltip Pattern](https://www.w3.org/WAI/ARIA/apg/patterns/tooltip/).
 *
 * **Migration from the old Tooltip/WrappedTooltip**
 *
 * * The new Tooltip component is a direct replacement for the old Tooltip/WrappedTooltip components.
 * * The 'children' prop is a content now, not an anchor element. The anchor element is the 'anchor' prop.
 * * If you need to click on the tooltip content, you can use the Dropdown or the Popover component instead.
 * * If you need to use mouse position to determine the tooltip position or else, consider using positionRef like in the Context Menu component.
 * * The 'type' prop is removed. Use the 'color' prop instead.
 * * Use native props for events, styles etc.
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/hover-tooltip--documentation}
 * @see [Changelog]{@link https://gitlab.com/pitchbook/pb/platform-fe/ui-kit/-/blob/master/ts-packages/kit/components/popups/CHANGELOG.md}
 */
export function Tooltip({
    anchor,
    children,
    popoverProps,
    color = 'dark',
    allowedPlacements,
    placement = allowedPlacements ? undefined : 'top',
    ...restProps
}: TooltipProps) {

    const {
        anchorRef, floatingElRef, getAnchorProps, getFloatingElProps, positionRef,
    } = useFloating({
        hoverEnabled: true,
        focusEnabled: true,
        role: 'tooltip',
        placement,
        allowedPlacements,
        verticalOffset: 8,
        ...restProps,
    });

    return (
        <>
            {anchor({
                anchorRef,
                getAnchorProps,
                positionRef,
            })}
            <FloatingPopover
                floatingFocusManagerProps={{disabled: true}}
                {...getFloatingElProps({
                    ...popoverProps,
                    className: classNames('tooltip', popoverProps?.className, {
                        tooltip_dark: color === 'dark',
                    }),
                })}
                ref={floatingElRef}>
                {children}
            </FloatingPopover>
        </>
    );
}
